import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, Box, Link, Typography } from '@mui/joy'
import { cleanupRequestStatus, getUsers } from 'features/usersSlice'
import { getProjects } from 'features/projectsSlice'
import { ROLES, ROLES_OPTIONS, SEND_MESSAGE_TYPES } from 'utils/config'
import { usersService } from 'services/usersService'
import { handleErrorMessage } from 'utils/helpers'
import routes from 'routes'
import usePagination from 'hooks/usePagination'
import useSearchParamValue from 'hooks/useSearchParamValue'
import Breadcrumbs from 'components/Breadcrumbs'
import FilterSearch from 'components/tables/FilterSearch'
import FilterSelect from 'components/tables/FilterSelect'
import FiltersWrapper from 'components/tables/FiltersWrapper'
import MainLayout from 'components/templates/MainLayout'
import PageLoader from 'components/PageLoader'
import DeleteModal from 'components/DeleteModal'
import EnchantedTable from 'components/tables/EnchantedTable'
import AdvancedTooltip from 'components/UI/AdvancedTooltip'
import UserEngagementTooltip from 'components/UI/UserEngagementTooltip'
import MessageUsersModal from 'components/MessageUsersModal'
import TablePaginationDesktop from 'components/tables/TablePaginationDesktop'

const headers = [
  { label: 'Imię', propName: 'firstName' },
  {
    label: 'Nazwisko',
    propName: 'lastName',
    type: 'computed',
    fn: (user) => {
      if (user.adminNote)
        return (
          <AdvancedTooltip
            title=""
            description={user.adminNote}
            sx={{ justifyContent: 'flex-start' }}
            iconColor="warning"
          >
            {user.lastName}
          </AdvancedTooltip>
        )
      return user.lastName
    },
  },
  {
    label: 'E-mail',
    propName: 'email',
    type: 'computed',
    fn: (user) => (user.email ? <Link href={`mailto:${user.email}`}>{user.email}</Link> : '-'),
  },
  { label: 'Telefon', propName: 'phoneNumber' },
  {
    label: 'Aktywność',
    propName: 'engagement',
    type: 'computed',
    className: 'width-md',
    fn: (user) => <UserEngagementTooltip user={user} />,
  },
  {
    label: 'Weryfikacja',
    propName: 'isEmailVerified',
    type: 'check',
    className: 'width-sm',
  },
  {
    label: 'Akceptacja',
    propName: 'isAccountConfirmed',
    type: 'check',
    className: 'width-sm',
  },
  {
    label: 'Umowa',
    propName: 'isBillingInfoComplete',
    type: 'check',
    className: 'width-xm',
  },
  {
    label: 'Blokada',
    propName: 'isBlocked',
    type: 'blocked',
    className: 'width-xm',
  },
]

const AdminUsersView = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const { users, loading: loadingUsers } = useSelector((s) => s.users)
  const { projects, loading: loadingProjects } = useSelector((s) => s.projects)

  const [innerLoading, setInnerLoading] = useState(false)
  const [blockUserObject, setBlockUserObject] = useState(false)
  const [deleteUserObject, setDeleteUserObject] = useState(false)
  const [deleteUsersArray, setDeleteUsersArray] = useState(false)
  const [messageUsersArray, setMessageUsersArray] = useState(false)
  const [message, setMessage] = useState(false)

  const [searchFilter, setSearchFilter] = useSearchParamValue('search', '')
  const [roleFilter, setRoleFilter] = useSearchParamValue('role', ROLES.user.value)
  const [projectFilter, setProjectFilter] = useSearchParamValue('project', '')

  const pagination = usePagination()

  const params = useMemo(
    () => ({
      role: roleFilter || null,
      search: searchFilter || null,
      limit: pagination.values.perPage,
      page: pagination.values.currentPage,
      project: projectFilter || null,
    }),
    [searchFilter, roleFilter, pagination.values.perPage, pagination.values.currentPage, projectFilter]
  )

  useEffect(() => {
    pagination.setPagination((prevPag) => ({ ...prevPag, ...users.pagination }))
  }, [users.pagination])

  useEffect(() => {
    dispatch(getUsers({ params }))
    return () => dispatch(cleanupRequestStatus())
  }, [dispatch, params])

  useEffect(() => {
    dispatch(getProjects())
  }, [dispatch])

  const handleGoToUserPage = (userId) => navigate(routes.adminUsersDetails.replace(':userId', userId))

  const handleActivateUser = (userId) => {
    usersService
      .adminUsersActivate(userId)
      .then((res) => {
        console.log(res)
        enqueueSnackbar('Użytownik został aktywowany', { variant: 'success' })
        dispatch(getUsers({ params }))
      })
      .catch((err) => {
        console.log(err)
        enqueueSnackbar(handleErrorMessage(err, 'Coś poszło nie tak.'), { variant: 'error' })
      })
  }

  const handleUnblockUser = (userId) => {
    setInnerLoading(true)
    usersService
      .unblockUser(userId)
      .then((res) => {
        console.log(res)
        enqueueSnackbar(res.data?.message || 'Użytkownik został odblokowany', { variant: 'success' })
        dispatch(getUsers({ params }))
      })
      .catch((err) => {
        console.log(err)
        enqueueSnackbar(handleErrorMessage(err, 'Coś poszło nie tak.'), { variant: 'error' })
      })
      .finally(() => setInnerLoading(false))
  }

  const handleDeleteUser = () => {
    setInnerLoading(true)

    let userId = deleteUserObject.id
    if (blockUserObject) userId = blockUserObject.id

    usersService
      .deleteUser(userId)
      .then((res) => {
        console.log(res)
        enqueueSnackbar(res.data?.message || 'Użytkownik został usunięty', { variant: 'success' })
        setDeleteUserObject(false)
        setBlockUserObject(false)
        dispatch(getUsers({ params }))
      })
      .catch((err) => {
        console.log(err)
        enqueueSnackbar(handleErrorMessage(err, 'Coś poszło nie tak.'), { variant: 'error' })
      })
      .finally(() => setInnerLoading(false))
  }

  const handleActivateBulk = (users) => {
    usersService
      .adminUsersActivateBulk(users.map((u) => u.id))
      .then((res) => {
        console.log(res)
        enqueueSnackbar('Użytkownicy zostali aktywowani', { variant: 'success' })
        dispatch(getUsers({ params }))
      })
      .catch((err) => {
        console.log(err)
        enqueueSnackbar('W wyniku błędu żaden z wybranych użytkowników nie został aktywowany.', {
          variant: 'error',
          autoHideDuration: 10000,
        })
        enqueueSnackbar(handleErrorMessage(err, 'Coś poszło nie tak.'), { variant: 'error', autoHideDuration: 10000 })
      })
      .finally(() => setInnerLoading(false))
  }

  const handleDeleteUsersBulk = () => {
    setInnerLoading(true)
    usersService
      .deleteUsersBulk(deleteUsersArray.map((u) => u.id))
      .then((res) => {
        console.log(res)
        enqueueSnackbar('Użytkownicy zostali usunięci.', { variant: 'success' })
        setDeleteUsersArray(false)
        dispatch(getUsers({ params }))
      })
      .catch((err) => {
        console.log(err)
        enqueueSnackbar('W wyniku błędu żaden z wybranych użytkowników nie został usunięty.', {
          variant: 'error',
          autoHideDuration: 10000,
        })
        enqueueSnackbar(handleErrorMessage(err, 'Coś poszło nie tak.'), { variant: 'error', autoHideDuration: 10000 })
      })
      .finally(() => setInnerLoading(false))
  }

  const handleSendMessage = (values, helpers) => {
    setInnerLoading(true)

    const { type, subject, ...restValues } = values
    const pieckedValues = { ...restValues }
    let method = null

    if (SEND_MESSAGE_TYPES.sms.value === type) method = usersService.adminSendSmsToProjectUsers
    else if (SEND_MESSAGE_TYPES.email.value === type) {
      method = usersService.adminSendEmailToProjectUsers
      pieckedValues.subject = subject
    }

    if (!method) return console.error('Typ wiadomości nie jest obsługiwany')

    method(
      messageUsersArray.map((u) => u.id),
      pieckedValues
    )
      .then((res) => {
        console.log(res)
        helpers.resetForm()
        setMessage('Wiadomość została wysłana.')
        enqueueSnackbar('Wiadomość została wysłana.', { variant: 'success' })
      })
      .catch((err) => {
        console.log(err)
        enqueueSnackbar(handleErrorMessage(err, 'Coś poszło nie tak.'), { variant: 'error' })
      })
      .finally(() => setInnerLoading(false))
  }

  if (loadingUsers || loadingProjects)
    return (
      <MainLayout>
        <PageLoader />
      </MainLayout>
    )

  return (
    <MainLayout>
      <MessageUsersModal
        open={messageUsersArray}
        setObject={(val) => setMessageUsersArray(val)}
        handleSubmit={handleSendMessage}
        loading={innerLoading}
      >
        <Typography>Wyślij wiadomość do użytkowników: </Typography>
        <Typography color="primary">
          {messageUsersArray &&
            messageUsersArray.map((user, index) => (
              <span key={user.id} title={user.email}>
                {user.firstName} {user.lastName}
                {index < messageUsersArray.length - 1 ? ', ' : ''}
              </span>
            ))}
        </Typography>
        {message && (
          <Alert color="success" variant="soft" sx={{ mt: 2, mb: 1 }}>
            {message}
          </Alert>
        )}
      </MessageUsersModal>
      <DeleteModal
        open={deleteUserObject || blockUserObject || deleteUsersArray}
        handleDelete={() => {
          if (deleteUserObject || blockUserObject) handleDeleteUser()
          else if (deleteUsersArray) handleDeleteUsersBulk()
        }}
        loading={innerLoading}
        setObject={(val) => {
          setDeleteUserObject(val)
          setBlockUserObject(val)
          setDeleteUsersArray(val)
        }}
        btnText={deleteUserObject || deleteUsersArray ? 'Usuń' : 'Zablokuj'}
      >
        {deleteUserObject && (
          <>
            Czy jesteś pewny, ze chcesz usunąć użytkownika:{' '}
            <Typography color="primary" fontWeight={500}>
              {deleteUserObject.email}
            </Typography>
          </>
        )}
        {blockUserObject && (
          <>
            Czy jesteś pewny, ze chcesz zablokować użytkownika:{' '}
            <Typography color="primary" fontWeight={500}>
              {blockUserObject.email}
            </Typography>
          </>
        )}
        {deleteUsersArray && <>Czy jesteś pewny, ze chcesz usunąć {deleteUsersArray.length} użytkowników?</>}
      </DeleteModal>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Breadcrumbs items={[{ label: 'Użytkownicy' }]} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          mb: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'start', sm: 'center' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography level="h2" component="h1">
          Użytkownicy
        </Typography>
      </Box>

      <FiltersWrapper
        renderSearch={
          <FilterSearch label="Wyszukiwanie użytkowników" value={searchFilter} setValue={setSearchFilter} />
        }
        renderFilters={
          <>
            <FilterSelect
              label="Rola"
              placeholder="Filtruj po roli"
              options={ROLES_OPTIONS}
              value={roleFilter}
              setValue={setRoleFilter}
            />
            <FilterSelect
              label="Udział w projekcie"
              placeholder="Filtruj po projekcie"
              options={projects.map((project) => ({ value: project.id, label: project.name }))}
              value={projectFilter}
              setValue={setProjectFilter}
            />
          </>
        }
      />

      <EnchantedTable
        headers={headers}
        data={users.data}
        offset={(pagination.values.currentPage - 1) * pagination.values.perPage}
        noDataText="Brak użytkowników do wyświetlenia."
        actions={[
          { label: 'Zobacz', func: (user) => handleGoToUserPage(user.id) },
          {
            label: 'Aktywuj',
            divider: true,
            func: (user) => handleActivateUser(user.id),
            display: (user) => !user.isAccountConfirmed,
          },
          {
            label: 'Zablokuj',
            color: 'warning',
            divider: true,
            func: (user) => setBlockUserObject(user),
            display: (user) => user.hasProblematicRelation && !user.isBlocked,
          },
          {
            label: 'Odblokuj',
            color: 'warning',
            divider: true,
            func: (user) => handleUnblockUser(user.id),
            display: (user) => user.isBlocked,
          },
          {
            label: 'Usuń',
            color: 'danger',
            divider: true,
            func: (user) => setDeleteUserObject(user),
            display: (user) => !user.hasProblematicRelation,
          },
        ]}
        bulkActions={[
          {
            label: 'Aktywuj użytkowników',
            color: 'primary',
            divider: true,
            func: (selectedUsers) => handleActivateBulk(selectedUsers),
          },
          {
            label: 'Wyślij wiadomość',
            color: 'warning',
            divider: true,
            func: (selectedUsers) => setMessageUsersArray(selectedUsers),
          },
          {
            label: 'Usuń użytkowników',
            color: 'danger',
            divider: true,
            func: (selectedUsers) => setDeleteUsersArray(selectedUsers),
          },
        ]}
      />
      <TablePaginationDesktop pagination={pagination} />
    </MainLayout>
  )
}

export default AdminUsersView
